"use client";

// DTC benefits step mocks (Figma 8196:57872, 8196:57977, 8196:58025) — live
// product-UI squares inside the Paid: Three Steps panel.

import { useLocale } from "next-intl";
import type { ReactNode, SVGProps } from "react";

import { PRODUCT_WIDGET_ASSETS } from "@/components/product-widgets/assets";
import {
  ProductWidgetFrame,
  ScaledProductWidgetCanvas,
} from "@/components/product-widgets/mock-frame";
import { formatLocalizedCurrency } from "@/lib/localize-currency";
import { cn } from "@/lib/utils";

const SPEND_ROWS: {
  className: string;
  chips: SpendChipConfig[];
}[] = [
  {
    className: "left-[4.66px] top-[118.22px] gap-[16.535px]",
    chips: [
      {
        label: "Marketing",
        icon: <IconSell className="size-[14px]" />,
        bg: "bg-[#fef2f2]",
        border: "border-[#ffcdb5]",
      },
      {
        label: "Inventory",
        icon: <IconDataTable className="size-3" />,
        bg: "bg-[#eafaee]",
        border: "border-[#abf0b7]",
      },
      {
        label: "Rent",
        icon: <IconHomeWork className="size-3" />,
        bg: "bg-[#f0f5ff]",
        border: "border-[#c6daff]",
      },
    ],
  },
  {
    className: "left-[-40.41px] top-[172.03px] gap-[14.985px] opacity-90",
    chips: [
      {
        label: "Other",
        icon: <IconAddCircle className="size-[14px]" />,
        bg: "bg-[#f0f5ff]",
        border: "border-[#c6daff]",
        muted: true,
      },
      {
        label: "Hiring",
        icon: <IconPeople className="size-[14px]" />,
        bg: "bg-[#ecf8f5]",
        border: "border-[#b6e9dc]",
        muted: true,
      },
      {
        label: "Operations",
        icon: <IconAccountTree className="size-3" />,
        bg: "bg-[#feebfd]",
        border: "border-[#ffc1fd]",
        muted: true,
      },
      {
        label: "Additional",
        icon: <IconAddCircle className="size-[14px]" />,
        bg: "bg-[#f0f5ff]",
        border: "border-[#c6daff]",
        muted: true,
      },
    ],
  },
  {
    className: "left-[-8px] top-[219.81px] gap-[16.535px] opacity-20",
    chips: [
      {
        label: "Growth",
        icon: <IconStackedBar className="size-4" />,
        bg: "bg-[#eafaee]",
        border: "border-[#abf0b7]",
        muted: true,
      },
      {
        label: "Shipping",
        icon: <IconPeople className="size-[14px]" />,
        bg: "bg-[#ecf8f5]",
        border: "border-[#b6e9dc]",
        muted: true,
      },
      {
        label: "Advertising",
        icon: <IconAdsClick className="size-4" />,
        bg: "bg-[#fef2f2]",
        border: "border-[#ffcdb5]",
        muted: true,
      },
    ],
  },
];

type SpendChipConfig = {
  label: string;
  icon: ReactNode;
  bg: string;
  border: string;
  muted?: boolean;
};

export function DtcSpendRestrictionsMock({
  className,
}: {
  className?: string;
}) {
  return (
    <ProductWidgetFrame className={className}>
      <ScaledProductWidgetCanvas>
        {SPEND_ROWS.map((row, i) => (
          <div
            key={i}
            className={cn("absolute flex w-max items-center", row.className)}
          >
            {row.chips.map((chip) => (
              <SpendChip key={chip.label} {...chip} />
            ))}
          </div>
        ))}
      </ScaledProductWidgetCanvas>
    </ProductWidgetFrame>
  );
}

export function DtcTransparentTermsMock({ className }: { className?: string }) {
  const locale = useLocale();
  const rows = [
    {
      label: "Financing amount",
      value: formatLocalizedCurrency(100_000, locale),
    },
    { label: "Fee", value: formatLocalizedCurrency(4_200, locale) },
  ];

  return (
    <ProductWidgetFrame className={className}>
      <ScaledProductWidgetCanvas>
        <div
          className="pointer-events-none absolute flex items-center justify-center"
          style={{ left: 213.38, top: 242.15, width: 19.902, height: 22.562 }}
        >
          <div className="-rotate-[15deg]">
            <img
              src={PRODUCT_WIDGET_ASSETS.cursor}
              alt=""
              className="h-[19.217px] w-[15.455px]"
              aria-hidden
            />
          </div>
        </div>

        <div
          className="absolute overflow-hidden rounded-[20.555px] bg-white p-[20.555px] shadow-[-18.842px_0px_54.813px_0px_rgba(0,0,0,0.07)]"
          style={{
            left: "calc(50% + 26.33px)",
            top: "calc(50% + 65px)",
            width: 383,
            transform: "translate(-50%, -50%)",
          }}
        >
          <div className="flex flex-col gap-[20.555px]">
            <div className="flex gap-2" aria-hidden>
              <span className="bg-grey-100 size-[15px] rounded-full" />
              <span className="bg-grey-100 size-[15px] rounded-full" />
              <span className="bg-grey-100 size-[15px] rounded-full" />
            </div>
            <div className="bg-grey-50 rounded-[13.703px] p-[34.258px]">
              <dl className="flex flex-col gap-[6.852px]">
                {rows.map(({ label, value }) => (
                  <div
                    key={label}
                    className="border-grey-100 flex items-center justify-between border-b py-[13.703px]"
                  >
                    <dt className="text-grey-600 text-[12px] leading-[1.4] tracking-[-0.24px]">
                      {label}
                    </dt>
                    <dd className="text-grey-600 text-[20px] leading-[1.4] tracking-[-0.4px]">
                      {value}
                    </dd>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-[17.129px]">
                  <dt className="text-grey-600 text-[12px] leading-[1.4] tracking-[-0.24px]">
                    Total to repay
                  </dt>
                  <dd className="text-grey-950 text-[20px] leading-[1.2] tracking-[-0.8px]">
                    {formatLocalizedCurrency(104_200, locale)}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </ScaledProductWidgetCanvas>
    </ProductWidgetFrame>
  );
}

const GROWTH_CHART_ASSETS = {
  area: "/assets-next/direct-to-consumer-financing/benefits/growth-chart-area.svg",
  line: "/assets-next/direct-to-consumer-financing/benefits/growth-chart-line.svg",
  grid: "/assets-next/direct-to-consumer-financing/benefits/growth-chart-grid.svg",
  gridShort:
    "/assets-next/direct-to-consumer-financing/benefits/growth-chart-grid-short.svg",
} as const;

/** Vertical grid ticks — Figma 8196:81188–81192, coords relative to inner card. */
const GROWTH_CHART_GRID_LINES = [
  { left: 28, height: 108 },
  { left: 82, height: 108 },
  { left: 136, height: 108 },
  { left: 190, height: 108 },
  { left: 244, height: 96, short: true },
] as const;

function GrowthChartGridLines() {
  return (
    <>
      {GROWTH_CHART_GRID_LINES.map((line) => (
        <div
          key={line.left}
          aria-hidden
          className="pointer-events-none absolute flex items-center justify-center"
          style={{ left: line.left, top: 62, width: 0, height: line.height }}
        >
          <div className="rotate-90">
            <img
              src={
                "short" in line && line.short
                  ? GROWTH_CHART_ASSETS.gridShort
                  : GROWTH_CHART_ASSETS.grid
              }
              alt=""
              width={line.height}
              height={1}
              className="block max-w-none"
            />
          </div>
        </div>
      ))}
    </>
  );
}

export function DtcGrowthPartnerMock({ className }: { className?: string }) {
  return (
    <ProductWidgetFrame className={className}>
      <ScaledProductWidgetCanvas>
        <div
          className={cn(
            "border-grey-100 absolute rounded-t-[24px] border border-b-0 bg-gradient-to-b from-[#f9f9f8] to-white shadow-[10px_14px_38px_0px_rgba(0,0,0,0.03)]",
            "max-md:top-auto max-md:bottom-0 md:top-[93.5px]"
          )}
          style={{ left: 30.67, width: 303, height: 276 }}
        >
          <p
            className="absolute text-[14px] leading-[1.5] tracking-[-0.02em] whitespace-nowrap"
            style={{ left: "calc(50% - 82.83px)", top: 27 }}
          >
            <span className="text-grey-950">Your growth.</span>{" "}
            <span className="text-grey-600">Our support.</span>
          </p>
        </div>
        <div
          className={cn(
            "border-grey-100 absolute rounded-t-2xl border border-b-0 bg-gradient-to-br from-white to-[#f4f4f4] shadow-[10px_14px_38px_0px_rgba(0,0,0,0.03)]",
            "max-md:top-auto max-md:bottom-0 md:top-[160.5px]"
          )}
          style={{ left: 46.67, width: 271, height: 209 }}
        >
          <div
            className="bg-grey-0 absolute flex items-start justify-between rounded-2xl px-3 py-2 shadow-[0px_4.713px_9.426px_rgba(24,37,75,0.08)]"
            style={{ left: 21, top: 58, width: 142, height: 48 }}
          >
            <div className="flex flex-col gap-0.5">
              <span className="text-grey-950 text-[10px] leading-[1.5] opacity-50">
                Funding approved
              </span>
              <span className="text-grey-950 text-[14px] leading-[1.5] tracking-[-0.04em]">
                $1,200,000
              </span>
            </div>
            <span className="bg-grey-0 flex size-[30px] shrink-0 items-center justify-center rounded-lg shadow-[0px_4.713px_18.852px_rgba(24,37,75,0.12)]">
              <IconSwap className="text-grey-500 size-4" />
            </span>
          </div>

          <GrowthChartGridLines />

          <img
            src={GROWTH_CHART_ASSETS.area}
            alt=""
            aria-hidden
            className="pointer-events-none absolute max-w-none"
            style={{ left: 28, top: 78.5, width: 216, height: 72.5 }}
          />
          <img
            src={GROWTH_CHART_ASSETS.line}
            alt=""
            aria-hidden
            className="pointer-events-none absolute max-w-none"
            style={{ left: 28, top: 78, width: 215, height: 65 }}
          />

          {["2023", "2024", "2025", "2026"].map((year, i) => (
            <span
              key={year}
              className="text-grey-500 absolute text-[10px] leading-[1.4]"
              style={{ left: 27 + i * 54, top: 160.5 }}
            >
              {year}
            </span>
          ))}

          <span
            className="border-grey-500 text-grey-950 absolute flex h-[21px] w-[38px] items-center justify-center rounded-full border-2 bg-white text-[10px] font-semibold tracking-[-0.2px]"
            style={{ left: 193, top: 48 }}
          >
            82%
          </span>
          <span
            className="bg-pulse-700 absolute size-[7px] rounded-full"
            style={{ left: 198, top: 87 }}
            aria-hidden
          />
        </div>
      </ScaledProductWidgetCanvas>
    </ProductWidgetFrame>
  );
}

const BENEFIT_MOCKS = [
  DtcSpendRestrictionsMock,
  DtcTransparentTermsMock,
  DtcGrowthPartnerMock,
] as const;

export function DtcBenefitStepMock({
  index,
  className,
}: {
  index: number;
  className?: string;
}) {
  const Mock = BENEFIT_MOCKS[index] ?? BENEFIT_MOCKS[0];
  return <Mock className={className} />;
}

function SpendChip({ label, icon, bg, border, muted }: SpendChipConfig) {
  return (
    <div className="bg-grey-0 flex shrink-0 items-center gap-[7px] rounded-full py-2 pr-3.5 pl-2 shadow-[0px_4.8px_24px_rgba(0,0,0,0.06)]">
      <span
        className={cn(
          "flex size-[21px] shrink-0 items-center justify-center rounded-full border",
          bg,
          border
        )}
        aria-hidden
      >
        {icon}
      </span>
      <span
        className={cn(
          "text-[16px] leading-[1.2] tracking-[-0.04em] whitespace-nowrap",
          muted ? "text-grey-950/40" : "text-grey-500"
        )}
      >
        {label}
      </span>
    </div>
  );
}

function IconSwap(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden
      {...props}
    >
      <path
        d="M20 9H4M7 6L4 9L7 12M4 15H20M17 12L20 15L17 18"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconSell(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden {...props}>
      <path
        d="M4.66667 4.66667H11.3333V11.3333H4.66667V4.66667ZM5.33333 3.33333H10.6667C11.403 3.33333 12 3.93029 12 4.66667V11.3333C12 12.0697 11.403 12.6667 10.6667 12.6667H5.33333C4.59695 12.6667 4 12.0697 4 11.3333V4.66667C4 3.93029 4.59695 3.33333 5.33333 3.33333Z"
        fill="#FFCDB5"
      />
    </svg>
  );
}

function IconDataTable(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden {...props}>
      <path
        d="M3.33333 3.33333H12.6667V12.6667H3.33333V3.33333ZM4.66667 4.66667V7.33333H7.33333V4.66667H4.66667ZM8.66667 4.66667V7.33333H11.3333V4.66667H8.66667ZM4.66667 8.66667V11.3333H7.33333V8.66667H4.66667ZM8.66667 8.66667V11.3333H11.3333V8.66667H8.66667Z"
        fill="#ABF0B7"
      />
    </svg>
  );
}

function IconHomeWork(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden {...props}>
      <path
        d="M2.66667 7.33333L8 3.33333L13.3333 7.33333V12.6667C13.3333 13.403 12.7364 14 12 14H4C3.26362 14 2.66667 13.403 2.66667 12.6667V7.33333Z"
        stroke="#C6DAFF"
        strokeWidth="1.2"
      />
    </svg>
  );
}

function IconAddCircle(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden {...props}>
      <circle cx="8" cy="8" r="6" stroke="#C6DAFF" strokeWidth="1.2" />
      <path
        d="M8 5.33333V10.6667M5.33333 8H10.6667"
        stroke="#C6DAFF"
        strokeWidth="1.2"
      />
    </svg>
  );
}

function IconPeople(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden {...props}>
      <circle cx="6" cy="5.5" r="2" stroke="#B6E9DC" strokeWidth="1.2" />
      <path
        d="M2.66667 12.6667C2.66667 10.4575 4.45753 8.66667 6.66667 8.66667H8C10.2091 8.66667 12 10.4575 12 12.6667"
        stroke="#B6E9DC"
        strokeWidth="1.2"
      />
      <circle cx="11" cy="5" r="1.5" stroke="#B6E9DC" strokeWidth="1.2" />
    </svg>
  );
}

function IconAccountTree(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden {...props}>
      <path
        d="M8 3.33333V6M8 10V12.6667M5.33333 8H3.33333M12.6667 8H10.6667M6.66667 6L5.33333 4.66667M9.33333 6L10.6667 4.66667M6.66667 10L5.33333 11.3333M9.33333 10L10.6667 11.3333"
        stroke="#FFC1FD"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconStackedBar(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden {...props}>
      <path
        d="M3.33333 12H5.33333V8H3.33333V12ZM7.33333 12H9.33333V5.33333H7.33333V12ZM11.3333 12H13.3333V3.33333H11.3333V12Z"
        fill="#ABF0B7"
      />
    </svg>
  );
}

function IconAdsClick(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden {...props}>
      <path
        d="M7.80667 11.9933C5.68667 11.8933 4 10.1467 4 8C4 5.79333 5.79333 4 8 4C10.1467 4 11.8933 5.68667 11.9933 7.80667L10.5933 7.38667C10.32 6.20667 9.26 5.33333 8 5.33333C6.52667 5.33333 5.33333 6.52667 5.33333 8C5.33333 9.26 6.20667 10.32 7.38667 10.5933L7.80667 11.9933Z"
        fill="#FFCDB5"
      />
    </svg>
  );
}
